/**
 * Referencia de atalhos, aberta com `?`.
 *
 * Existe porque a fase 8 entregou os atalhos e nenhum lugar onde descobri-los:
 * quem nao leu o commit nao tinha como saber que `E` conclui e `S` adia. Um
 * atalho que ninguem conhece nao e um recurso, e codigo morto com teclado.
 *
 * O catalogo abaixo e escrito a mao, com uma excecao: as teclas de navegacao
 * saem de `useNavItems`, a MESMA fonte que o handler usa para navegar. Sem isso,
 * acrescentar uma aba mudaria o comportamento de `1`-`5` e deixaria esta tela
 * mentindo — e a tela de ajuda errada e pior que a ausencia dela.
 */

import { useEffect, useRef } from 'react';
import { useNavItems, type NavItem } from '../../navigation.js';

interface Atalho {
  teclas: string[];
  descricao: string;
}

interface Secao {
  titulo: string;
  atalhos: Atalho[];
}

/** `⌘` no Mac, `Ctrl` no resto. Mostrar os dois numa linha so confunde. */
function teclaDeComando(): string {
  if (typeof navigator === 'undefined') return 'Ctrl';
  return /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? '⌘' : 'Ctrl';
}

function montarSecoes(abas: readonly NavItem[]): Secao[] {
  const cmd = teclaDeComando();

  return [
    {
      titulo: 'Geral',
      atalhos: [
        { teclas: [cmd, 'K'], descricao: 'Abrir a paleta de comandos' },
        { teclas: ['/'], descricao: 'Buscar' },
        { teclas: ['N'], descricao: 'Nova tarefa' },
        { teclas: ['?'], descricao: 'Esta lista' },
        { teclas: ['Esc'], descricao: 'Fechar o que estiver aberto' },
      ],
    },
    {
      titulo: 'Ir para',
      atalhos: abas.map((item, indice) => ({
        teclas: [String(indice + 1)],
        descricao: item.label,
      })),
    },
    {
      titulo: 'Sobre a tarefa em foco',
      atalhos: [
        { teclas: ['E'], descricao: 'Concluir' },
        { teclas: ['S'], descricao: 'Adiar para amanhã às 09:00' },
      ],
    },
    {
      titulo: 'Na entrada rápida',
      atalhos: [
        { teclas: ['#'], descricao: 'Marcar uma tag existente' },
        { teclas: ['*'], descricao: 'Escolher a lista' },
        { teclas: ['!'], descricao: 'Definir prioridade (!alta, !media, !baixa)' },
      ],
    },
  ];
}

interface ShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

export function ShortcutsHelp({ open, onClose }: ShortcutsHelpProps) {
  const fecharRef = useRef<HTMLButtonElement>(null);
  const abas = useNavItems();

  // O foco vai para o botao de fechar ao abrir. Sem isso, o leitor de tela
  // continua anunciando a tela de tras e o Tab passeia pelo conteudo coberto.
  useEffect(() => {
    if (open) fecharRef.current?.focus();
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-[10vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Atalhos de teclado"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80dvh] w-full max-w-lg flex-col overflow-y-auto rounded bg-surface p-4 shadow-card"
        // O clique dentro nao pode fechar: sem isto, selecionar texto e soltar
        // o botao sobre o fundo derrubaria o dialogo.
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-baseline justify-between gap-3 pb-3">
          <h2 className="font-display text-md font-semibold">Atalhos</h2>
          <button
            ref={fecharRef}
            type="button"
            onClick={onClose}
            className="min-h-touch rounded-sm px-2 text-sm text-text-mut hover:bg-surface-2"
          >
            Fechar
          </button>
        </div>

        <div className="flex flex-col gap-4">
          {montarSecoes(abas).map((secao) => (
            <section key={secao.titulo} className="flex flex-col gap-1">
              <h3 className="text-xs font-medium uppercase tracking-wide text-text-mut">
                {secao.titulo}
              </h3>

              <dl className="flex flex-col">
                {secao.atalhos.map((atalho) => (
                  <div
                    key={atalho.descricao}
                    className="flex items-center justify-between gap-3 border-b border-border py-1.5 last:border-b-0"
                  >
                    <dt className="text-sm">{atalho.descricao}</dt>
                    <dd className="flex shrink-0 gap-1">
                      {atalho.teclas.map((tecla) => (
                        <kbd
                          key={tecla}
                          className="rounded-sm border border-border bg-surface-2 px-1.5 py-0.5 font-mono text-xs"
                        >
                          {tecla}
                        </kbd>
                      ))}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ))}
        </div>

        <p className="pt-3 text-xs text-text-mut">
          As teclas de uma letra só valem fora de campos de texto — do contrário, escrever
          &ldquo;nome&rdquo; trocaria de aba a cada letra.
        </p>
      </div>
    </div>
  );
}
